import { EventType } from '@atlas/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import type { CreateMessageInput, Message, MessageListParams, UpdateMessageInput } from '@/domain/message.types'
import { MESSAGE_LIST_MAX_LIMIT, MessageTranslationStatus } from '@/domain/message.types'
import { RealtimeConnectionState, subscribeRealtime, useRealtimeConnectionState } from '@/realtime/realtime-dispatcher'
import { messageApi } from '@/services/message.service'

/** React-query hooks for the message CRUD pages. */

/** Statuses a translation job no longer moves out of — polling can stop once every row is here. */
const TERMINAL_TRANSLATION_STATUSES: ReadonlySet<MessageTranslationStatus | null | undefined> = new Set([
	MessageTranslationStatus.READY,
	MessageTranslationStatus.FAILED,
	null,
	undefined,
])

/** Poll cadence while a translation job is still `pending`/`translating` — mirrors useWorkOrderValidationAssets. */
const REFETCH_INTERVAL_MS = 5000

/**
 * A job normally resolves (ready/failed) within seconds. Past this, it's
 * presumed dead (worker crash, exhausted DLQ retries) rather than genuinely
 * in flight — exported (along with `isNonTerminal`/`isStuck` below) so
 * `TranslationsSection.tsx`'s stuck-job Retry affordance shares the EXACT
 * same threshold and formula as the polling stop condition here; two
 * independent implementations of one invariant could silently drift apart
 * (the UI offering "Retry" while this hook kept polling forever underneath
 * it, or vice versa).
 */
export const STUCK_AFTER_MS = 2 * 60 * 1000

export const isNonTerminal = (status: MessageTranslationStatus | null | undefined) =>
	!TERMINAL_TRANSLATION_STATUSES.has(status)

/** A non-terminal job that's sat unchanged past `STUCK_AFTER_MS` — presumed dead, not genuinely in flight. */
export const isStuck = (status: MessageTranslationStatus | null | undefined, updatedAt: string) =>
	isNonTerminal(status) && Date.now() - new Date(updatedAt).getTime() > STUCK_AFTER_MS

/** The events that end a translation wait, either way. Exported so every surface that waits on a translation subscribes to the same pair. */
export const TRANSLATION_EVENTS = [EventType.MESSAGE_TRANSLATED, EventType.MESSAGE_TRANSLATION_FAILED]

/** Root of the message-list key family. Exported so other hooks and the realtime query bridge invalidate the same key. */
export const MESSAGES_KEY = 'messages'

/** `enabled` lets a page hold a list query it only sometimes needs (e.g. one of two mutually exclusive views) without issuing the request on every render. Defaults to always-on. */
export const useMessages = (params?: MessageListParams, options?: { enabled?: boolean }) =>
	useQuery({
		queryKey: [MESSAGES_KEY, params ?? {}],
		queryFn: () => messageApi.list(params),
		enabled: options?.enabled ?? true,
	})

/** Root of the single-message key family. Exported so the realtime query bridge invalidates the whole family on reconnect. */
export const MESSAGE_KEY = 'message'

/** Query key for one message's full record. Exported so the realtime query bridge invalidates the same key. */
export const messageKey = (messageId: string) => [MESSAGE_KEY, messageId]

/**
 * A single message. Waits on ITS OWN translation job — the Translations
 * section's group query already tracks the row's status, but this page's own
 * Content panel reads from this query, not that one, so without its own wait a
 * completed translation's text only ever showed up after a manual reload even
 * though the status badge had already updated. Two ways of waiting, chosen by
 * connection state (ADR-0020):
 *
 * - **Connected** — a subscription to this message's translation events.
 * - **Otherwise** — poll every 5s while the job is in flight, giving up once it
 *   is terminal or presumed stuck. The `isStuck` bound stays on this branch
 *   only: it exists because a job that never completes would otherwise poll
 *   forever, and a subscription costs nothing to keep waiting.
 */
export const useMessage = (id: string | null) => {
	const queryClient = useQueryClient()
	const isPushing = useRealtimeConnectionState() === RealtimeConnectionState.CONNECTED

	useEffect(() => {
		if (!isPushing || !id) return

		// Invalidate and let the query re-read: the event says the job ended, not
		// what it produced. The user-facing error for a failure comes from the
		// notification bridge.
		return subscribeRealtime({ type: TRANSLATION_EVENTS, entityRef: { id } }, () => {
			void queryClient.invalidateQueries({ queryKey: messageKey(id) })
		})
	}, [isPushing, id, queryClient])

	return useQuery({
		queryKey: messageKey(id as string),
		queryFn: () => messageApi.get(id as string),
		enabled: !!id,
		refetchInterval: (query) => {
			if (isPushing) return false // the subscription does the waiting
			const data = query.state.data
			if (!data) return REFETCH_INTERVAL_MS
			return isNonTerminal(data.translationStatus) && !isStuck(data.translationStatus, data.updatedAt)
				? REFETCH_INTERVAL_MS
				: false
		},
	})
}

/** Invalidate both the list and single-message caches after a mutation. */
const invalidateMessages = (qc: ReturnType<typeof useQueryClient>) => {
	void qc.invalidateQueries({ queryKey: [MESSAGES_KEY] })
	void qc.invalidateQueries({ queryKey: [MESSAGE_KEY] })
}

export const useCreateMessage = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (body: CreateMessageInput) => messageApi.create(body),
		onSuccess: () => invalidateMessages(qc),
	})
}

export const useUpdateMessage = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, patch }: { id: string; patch: UpdateMessageInput }) => messageApi.update(id, patch),
		onSuccess: () => invalidateMessages(qc),
	})
}

export const useDeleteMessage = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => messageApi.remove(id),
		onSuccess: () => invalidateMessages(qc),
	})
}

/** Query key for a translation group's locale variants. */
/** Root of the translation-group key family. Exported so the realtime query bridge invalidates the whole family. */
export const TRANSLATION_GROUP_KEY = 'translation-group'

export const translationGroupKey = (translationGroupId: string) => [TRANSLATION_GROUP_KEY, translationGroupId]

/**
 * Shared query definition for ONE translation group's locale variants — reused
 * by {@link useTranslationGroup} (a single group) and by the Messages page's
 * resolution matrix (many groups, via `useQueries`). Sharing it keeps the
 * polling stop condition — the real invariant — with exactly one
 * implementation; two copies could drift into polling forever on one surface
 * while the other had already given up.
 *
 * The group is read at the list endpoint's maximum page size: a group holds one
 * row per locale, and the endpoint's default page of 20 would silently truncate
 * a widely-localized message.
 */
export function translationGroupQuery(translationGroupId: string, isPushing: boolean) {
	return {
		queryKey: translationGroupKey(translationGroupId),
		queryFn: () => messageApi.list({ translationGroupId, limit: MESSAGE_LIST_MAX_LIMIT }),
		refetchInterval: (query: { state: { data?: Message[] } }) => {
			if (isPushing) return false as const // the subscription does the waiting
			const rows = query.state.data ?? []
			const anyActivelyPending = rows.some(
				(m) => isNonTerminal(m.translationStatus) && !isStuck(m.translationStatus, m.updatedAt),
			)
			return anyActivelyPending ? REFETCH_INTERVAL_MS : (false as const)
		},
	}
}

/** Every variant with a still-running job, across any number of groups — what a realtime subscription must wait on. */
export const pendingVariants = (variants: readonly Message[]): Message[] =>
	variants.filter((m) => isNonTerminal(m.translationStatus))

/**
 * A translation group's locale variants (source + every translation). Waits for
 * the worker to finish each in-flight row, two ways depending on connection
 * state (ADR-0020):
 *
 * - **Connected** — a subscription to the translation events of the rows still
 *   in flight. No interval, so a slow translation costs nothing while it runs.
 * - **Otherwise** — poll every 5s while any row is non-terminal AND not yet
 *   presumed stuck. The stuck bound stays on this branch only: a job that's
 *   actually dead (worker crash, exhausted DLQ retries) must not keep the page
 *   polling forever just because it never reached a terminal status
 *   server-side, whereas a subscription can wait indefinitely for free.
 */
export const useTranslationGroup = (translationGroupId: string | null) => {
	const queryClient = useQueryClient()
	const isPushing = useRealtimeConnectionState() === RealtimeConnectionState.CONNECTED

	const query = useQuery<Message[]>({
		...translationGroupQuery(translationGroupId ?? '', isPushing),
		enabled: !!translationGroupId,
	})

	// A string, not an array: the query returns a fresh array on every refetch, so
	// an array dependency would re-register the subscription each time. Joining the
	// ids makes an unchanged set compare equal.
	const pendingIdKey = pendingVariants(query.data ?? [])
		.map((m) => m.messageId)
		.join(',')

	useEffect(() => {
		const pendingMessageIds = pendingIdKey ? pendingIdKey.split(',') : []
		if (!isPushing || !translationGroupId || pendingMessageIds.length === 0) return

		// Invalidate the whole group rather than the row the event names: a variant
		// finishing changes the group's aggregate state, and re-reading is what
		// produces the translated text — the event does not carry it.
		return subscribeRealtime({ type: TRANSLATION_EVENTS, entityRef: { id: pendingMessageIds } }, () => {
			void queryClient.invalidateQueries({ queryKey: translationGroupKey(translationGroupId) })
		})
	}, [isPushing, pendingIdKey, translationGroupId, queryClient])

	return query
}

/** Request an AI translation of `messageId` into one or more target locales. */
export const useRequestTranslation = (messageId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (targetLocales: string[]) => messageApi.requestTranslation(messageId, targetLocales),
		onSuccess: (rows) => {
			const translationGroupId = rows[0]?.translationGroupId
			if (!translationGroupId) return
			const key = translationGroupKey(translationGroupId)
			// Write the server's own response (already `translationStatus: 'pending'`)
			// straight into the cache — otherwise there's a window between this
			// mutation resolving and the subsequent invalidated refetch landing
			// where the cached row still shows its pre-request status.
			qc.setQueryData<Message[]>(key, (current) => {
				if (!current) return current
				const byId = new Map(rows.map((r) => [r.messageId, r]))
				const updated = current.map((m) => byId.get(m.messageId) ?? m)
				const newlyCreated = rows.filter((r) => !current.some((m) => m.messageId === r.messageId))
				return [...updated, ...newlyCreated]
			})
			void qc.invalidateQueries({ queryKey: key })
		},
	})
}

/**
 * Batch variant of {@link useRequestTranslation} — one shared target-locale
 * set applied to many source messages in one call (Campaign Detail's
 * batch-translate trigger). Unlike the single-message mutation, this does NOT
 * optimistically write into any translation-group cache: each item's outcome
 * may be a success (with target rows) or a best-effort error signal that
 * doesn't guarantee nothing was queued (see `messageApi.requestTranslationBatch`'s
 * own doc comment) — so every successful item's group is simply invalidated,
 * letting the existing `useTranslationGroup` polling/realtime path re-derive
 * the true state, exactly as the single-message UI already relies on it to.
 */
export const useRequestTranslationBatch = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (items: { messageId: string; targetLocales: string[] }[]) => messageApi.requestTranslationBatch(items),
		onSuccess: (results) => {
			for (const result of results) {
				const translationGroupId = result.targets?.[0]?.translationGroupId
				if (!translationGroupId) continue
				void qc.invalidateQueries({ queryKey: translationGroupKey(translationGroupId) })
			}
		},
	})
}
